import type { User } from "@book-club/shared";
import { hasAccess, isFlaggedForRemoval } from "@book-club/shared";
import { format } from "date-fns";
import { INACTIVITY_THRESHOLD_MONTHS } from "@/features/members/constants/inactivity";

export type SortColumn =
    | "name"
    | "phone"
    | "lastAttended"
    | "monthsSinceLastAttendance"
    | "totalAttended"
    | "totalNoShows"
    | "cancellations"
    | "lateCancellations";

export type FilterType =
    | "flagged"
    | "cancellations"
    | "lateCancellations"
    | "noShows"
    | "waitlisted"
    | "inactive"
    | "hasBirthday"
    | "noBirthday";

/**
 * Formats a date for display in member directory/profile
 * Returns "Never" if date is null or undefined
 */
export function formatMemberDate(date: Date | null | undefined): string {
    if (!date) {
        return "Never";
    }
    return format(new Date(date), "d MMM yyyy");
}

/**
 * Formats a birthday { month, day } for display, e.g. "14 March".
 * Returns "Not set" if null/undefined.
 */
export function formatBirthday(birthday: { month: number; day: number } | null | undefined): string {
    if (!birthday) {
        return "Not set";
    }
    return format(new Date(2000, birthday.month - 1, birthday.day), "d MMMM");
}

/**
 * Calculates total cancellations (regular + late) for a user
 */
export function getTotalCancellations(user: User): number {
    const regular = user.datesOfEarlyCancellation?.length ?? 0;
    const late = user.datesOfLateCancellation?.length ?? 0;
    return regular + late;
}

function getMonthDifference(fromDate: Date, now: Date): number {
    const yearDifference = now.getFullYear() - fromDate.getFullYear();
    const monthDifference = now.getMonth() - fromDate.getMonth();
    return yearDifference * 12 + monthDifference;
}

/**
 * Calculates full calendar months since the member last attended.
 * Falls back to accessGrantedAt when there is no attendance history.
 * Returns null for members without booking eligibility.
 */
export function getMonthsSinceLastAttendance(user: User, now: Date = new Date()): number | null {
    const referenceDate = user.lastAttendedAt ?? user.accessGrantedAt ?? null;

    if (!referenceDate) {
        return null;
    }

    return Math.max(0, getMonthDifference(new Date(referenceDate), now));
}

export function isInactivityFlagged(
    user: User,
    thresholdMonths: number = INACTIVITY_THRESHOLD_MONTHS,
    now: Date = new Date()
): boolean {
    const monthsSinceLastAttendance = getMonthsSinceLastAttendance(user, now);
    return monthsSinceLastAttendance !== null && monthsSinceLastAttendance >= thresholdMonths;
}

/**
 * Filters out archived members (base filter applied before all other filters)
 * This is a safety net in addition to service-layer filtering
 */
export function excludeArchivedMembers(members: User[]): User[] {
    return members.filter((member) => !member.archivedAt);
}

/**
 * Filters members by search query (name or phone)
 */
export function filterMembers(members: User[], searchQuery: string): User[] {
    if (!searchQuery.trim()) {
        return members;
    }

    const query = searchQuery.toLowerCase();
    return members.filter(
        (member) =>
            member.displayName?.toLowerCase().includes(query) || member.phoneNumber?.toLowerCase().includes(query)
    );
}

/**
 * Gets the sort value for a member based on the sort column
 */
function getSortValue(member: User, sortColumn: SortColumn): string | number {
    switch (sortColumn) {
        case "name":
            return member.displayName ?? "";
        case "phone":
            return member.phoneNumber ?? "";
        case "lastAttended":
            return member.lastAttendedAt ? new Date(member.lastAttendedAt).getTime() : 0;
        case "monthsSinceLastAttendance":
            return getMonthsSinceLastAttendance(member) ?? -1;
        case "totalAttended":
            return member.totalSessionsAttended ?? 0;
        case "totalNoShows":
            return member.datesOfAbsence?.length ?? 0;
        case "cancellations":
            return member.datesOfEarlyCancellation?.length ?? 0;
        case "lateCancellations":
            return member.datesOfLateCancellation?.length ?? 0;
    }
}

/**
 * Sorts members by the specified column and direction
 */
export function sortMembers(members: User[], sortColumn: SortColumn, sortDirection: "asc" | "desc"): User[] {
    return [...members].sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);

        if (aValue < bValue) {
            return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
    });
}

/**
 * Applies member filters based on active filter types
 */
export function applyMemberFilters(members: User[], activeFilters: Set<FilterType>): User[] {
    if (activeFilters.size === 0) {
        return members;
    }

    return members.filter((member) => {
        // Any active filter must match (OR logic)
        for (const filterType of activeFilters) {
            let matches = false;

            switch (filterType) {
                case "flagged":
                    matches = isFlaggedForRemoval(member);
                    break;
                case "cancellations":
                    matches = getTotalCancellations(member) > 0;
                    break;
                case "lateCancellations":
                    matches = (member.datesOfLateCancellation?.length ?? 0) > 0;
                    break;
                case "noShows":
                    matches = (member.datesOfAbsence?.length ?? 0) > 0;
                    break;
                case "waitlisted":
                    matches = !hasAccess(member);
                    break;
                case "inactive":
                    matches = isInactivityFlagged(member);
                    break;
                case "hasBirthday":
                    matches = !!member.birthday;
                    break;
                case "noBirthday":
                    matches = !member.birthday;
                    break;
            }

            if (matches) {
                return true; // Include if ANY filter matches
            }
        }

        return false; // Exclude if NO filters match
    });
}

/**
 * Gets filter counts for each filter type
 */
export function getFilterCounts(members: User[]): Record<FilterType, number> {
    return {
        cancellations: members.filter((member) => getTotalCancellations(member) > 0).length,
        flagged: members.filter((member) => isFlaggedForRemoval(member)).length,
        hasBirthday: members.filter((member) => !!member.birthday).length,
        inactive: members.filter((member) => isInactivityFlagged(member)).length,
        lateCancellations: members.filter((member) => (member.datesOfLateCancellation?.length ?? 0) > 0).length,
        noBirthday: members.filter((member) => !member.birthday).length,
        noShows: members.filter((member) => (member.datesOfAbsence?.length ?? 0) > 0).length,
        waitlisted: members.filter((member) => !hasAccess(member)).length,
    };
}
